import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import ThemeToggle from '../ThemeToggle/ThemeToggle';
import './Auth.css';

const Login = ({ onSwitchToRegister }) => {
  const { login, error, clearError } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [localError, setLocalError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError('');
    clearError();

    if (!email || !password) {
      setLocalError('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      await login(email, password);
    } catch (err) {
      setLocalError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      {/* Left - Brand / Marketing */}
      <div className="auth-hero">
        <div className="auth-brand">Mend</div>

        <div className="auth-hero-content">
          <div>
            <h1 className="auth-hero-title">
              Meet <em>Elo</em>
            </h1>
            <p className="auth-hero-subtitle">
              Your AI-powered guide to understanding yourself, your relationships, and your team dynamics.
            </p>
          </div>

          <div>
            <div className="auth-section-label" style={{ marginBottom: 16 }}>What's Inside</div>
            <ul className="auth-feature-list">
              <li className="auth-feature-row">
                <span className="auth-feature-num">01</span>
                <svg className="auth-feature-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M12 3l1.8 4.2L18 9l-4.2 1.8L12 15l-1.8-4.2L6 9l4.2-1.8L12 3z" />
                  <path d="M19 14l.9 2.1L22 17l-2.1.9L19 20l-.9-2.1L16 17l2.1-.9L19 14z" />
                </svg>
                <div className="auth-feature-text">
                  <h4>Personal Insights</h4>
                  <p>Discover your unique personality traits.</p>
                </div>
              </li>
              <li className="auth-feature-row">
                <span className="auth-feature-num">02</span>
                <svg className="auth-feature-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
                <div className="auth-feature-text">
                  <h4>Relationship Dynamics</h4>
                  <p>Understand how you connect with others.</p>
                </div>
              </li>
              <li className="auth-feature-row">
                <span className="auth-feature-num">03</span>
                <svg className="auth-feature-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                </svg>
                <div className="auth-feature-text">
                  <h4>Thoughtful Guidance</h4>
                  <p>Calm, honest advice — anytime you need it.</p>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Right - Form */}
      <div className="auth-form-section">
        <ThemeToggle className="auth-theme-toggle" />

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="auth-section-label auth-section-label--dot">Sign In</div>
          <h2>
            Welcome <em>back</em>
          </h2>
          <p className="auth-form-subtitle">Sign in to continue your journey with Elo.</p>

          {(localError || error) && (
            <div className="auth-error">{localError || error}</div>
          )}

          <div className="auth-field">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              disabled={loading}
              autoComplete="email"
            />
          </div>

          <div className="auth-field">
            <label htmlFor="password">Password</label>
            <div className="password-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                disabled={loading}
                autoComplete="current-password"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          <div className="auth-actions">
            <button type="submit" className="auth-button" disabled={loading}>
              {loading ? (
                <>
                  <svg className="spinner" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" opacity="0.25" />
                    <path d="M12 2a10 10 0 0 1 10 10" />
                  </svg>
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </button>

            <button type="button" className="auth-switch" onClick={onSwitchToRegister}>
              Get Started →
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login;
